import React, { useState } from "react";
import styles from "./SampleList.module.scss";

import moment from "moment";
import { SamplesProps } from "../../types/sample";
import { useAppDispatch } from "../../app/hooks";
import {
  setEditSample,
  toggleSampleModal,
} from "../../features/samples/samplesSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { GET } from "../../services/axios.service";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";
import SamplePaymentModal from "./SamplePaymentModal";
import { MdOutlinePayment } from "react-icons/md";

type Props = {
  samples: SamplesProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteSample: (id: number) => void;
  onPaymentsChanged: () => void;
};

const SampleList = ({
  samples,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteSample,
  onPaymentsChanged,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);
  const [paymentSample, setPaymentSample] = useState<SamplesProps | null>(
    null
  );

  const handleEditSample = async (sample: SamplesProps) => {
    await dispatch(setEditSample(sample));
    dispatch(toggleSampleModal(true));
  };

  const handleDownloadPdf = async (id: number) => {
    try {
      setIsActionLoading(id);
      const response: any = await GET(`/v1/admin/sample-orders/${id}/pdf`, {});
      if (response.data.status === "success") {
        const downloadLink = response.data.data.download_link;
        try {
          const newWindow = window.open(downloadLink, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
          }
        } catch (e) {
          window.location.href = downloadLink;
        }
      } else {
        toast.error(response.data.message, {
          ...(toastStyles as any),
        });
      }
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
        {
          ...(toastStyles as any),
        }
      );
    } finally {
      setIsActionLoading(0);
    }
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!"
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteSample(id);
      setIsActionLoading(0);
    }
  };

  return (
    <>
      <div className={styles.SampleList}>
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      Sr. No.
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      ઓર્ડર ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      પાર્ટી
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      આઈટમ
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      પ્રોસેસ
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      વજન
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      પરત
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      ટોટલ રૂ
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      સ્ટેટ્સ
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-base text-primary-1 font-bold tracking-wider"
                    >
                      તારીખ
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-base text-primary-1 font-bold tracking-wider"
                    >
                      એકશન્સ બટન
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={11}
                        className="text-primary-2 font-bold text-xl text-center py-2"
                      >
                        રાહ જુઓ...
                      </td>
                    </tr>
                  ) : (
                    <>
                      {samples.length > 0 ? (
                        <>
                          {samples.map((sample, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {(pageNumber - 1) * pageSize + index + 1}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample.id || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample?.party_name || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample?.item_name || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample?.process_name || "-"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample?.unit === 1 ? (
                                  <>{(sample?.qty || 0).toFixed(3)} KG</>
                                ) : sample?.unit === 2 ? (
                                  <>{sample?.qty || 0} Piece</>
                                ) : (
                                  <>{sample?.qty || 0} Pair</>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {(sample?.status || 0) === 1 && (
                                  <>
                                    {sample?.unit === 1 ? (
                                      <>
                                        {(sample?.delivered_qty || 0).toFixed(3)}{" "}
                                        KG
                                      </>
                                    ) : sample?.unit === 2 ? (
                                      <>{sample?.delivered_qty || 0} Piece</>
                                    ) : (
                                      <>{sample?.delivered_qty || 0} Pair</>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="text-right px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {(sample?.status || 0) === 1 && (
                                  <div className="flex items-center justify-end gap-2">
                                    <span
                                      className={
                                        sample?.payment_status === 1
                                          ? "text-green-600 font-bold"
                                          : ""
                                      }
                                    >
                                      {(sample?.amount || 0).toFixed(2)}
                                    </span>
                                    <MdOutlinePayment
                                      onClick={() => setPaymentSample(sample)}
                                      title="પેમેન્ટ ની વિગતો"
                                      className="cursor-pointer text-xl text-indigo-600 hover:text-indigo-900"
                                    />
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {sample?.status === 1
                                  ? "પૂરું થયેલું કામ"
                                  : "ચાલુ કામ"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {moment(sample.created_at).format(
                                  "DD-MM-YYYY hh:mm A"
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {isActionLoading === sample.id ? (
                                  <p className="text-primary-2 font-bold text-right text-lg">
                                    રાહ જુઓ...
                                  </p>
                                ) : (
                                  <>
                                    {sample?.status === 1 && (
                                      <button
                                        onClick={() =>
                                          handleDownloadPdf(sample.id)
                                        }
                                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                                      >
                                        PDF
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleEditSample(sample)}
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteRow(sample.id)}
                                      className="text-red-600 hover:text-red-900 ml-4"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      ) : (
                        <tr>
                          <td
                            colSpan={11}
                            className="text-primary-2 font-bold text-xl text-center py-2"
                          >
                            સેમ્પલ ઓર્ડર ની વિગતો મળી નથી
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {paymentSample && (
        <SamplePaymentModal
          sample={paymentSample}
          onClose={() => setPaymentSample(null)}
          onChanged={onPaymentsChanged}
        />
      )}
    </>
  );
};
export default SampleList;
